import 'bootstrap-icons/font/bootstrap-icons.css';
import '@content/general.css';
import '@content/icon.css';
import '@content/site.css';
import {ApplicationEnum} from '@mains/shared/constants.ts';
import {PageGlobals} from '@utils/page-globals.ts';
import {bindSiteHeaderActions} from './site-header-actions.ts';

type LegacyHelpMenuItem = {
    visible?: boolean;
    separator?: boolean;
    text?: string;
    url?: string;
    target?: string;
    enabled?: boolean;
};

type LegacyObservable<T> = {
    (): T;
    subscribe?: (callback: () => void) => void;
};

type LegacyLoginModel = {
    ApplicationType?: ApplicationEnum|null;
};

document.addEventListener('DOMContentLoaded', () => {
    const toggleButton = document.querySelector<HTMLButtonElement>('.menuToggle');
    const mobileMenuLauncher = document.querySelector<HTMLButtonElement>('.mobileMenuLauncher');
    const appShell = document.querySelector<HTMLElement>('.app-shell');
    const siteHeaderContent = document.querySelector<HTMLElement>('#site-header .app-topbar-content');
    const titleGroupElement = siteHeaderContent?.querySelector<HTMLElement>('.app-topbar-title-group') ?? null;
    const titleElement = titleGroupElement?.querySelector<HTMLElement>('.app-topbar-title') ?? null;
    const subnavItems = Array.from(document.querySelectorAll<HTMLElement>('.sidebar-nav-item-with-subnav'));
    const subnavToggles = Array.from(document.querySelectorAll<HTMLAnchorElement>('.sidebar-nav-item-with-subnav > .sidebar-nav-link[data-subnav-toggle]'));
    const helpMenuContainer = document.querySelector<HTMLElement>('[data-help-menu-container]');
    const helpMenuList = document.querySelector<HTMLUListElement>('[data-help-menu-list]');
    const ascWindow = window as Window & {
        asc?: {
            App?: {
                helpItems?: LegacyObservable<LegacyHelpMenuItem[]>;
                showHelpItems?: LegacyObservable<boolean>;
            };
        };
        loginModel?: LegacyLoginModel;
    };

    let fallbackHelpItems: LegacyHelpMenuItem[]|null = null;
    const fallbackShowHelpItems = true;

    const getFallbackApplicationType = (): ApplicationEnum|null => {
        if (PageGlobals.layoutModel.ApplicationType != null)
            return PageGlobals.layoutModel.ApplicationType;

        if (ascWindow.loginModel?.ApplicationType != null)
            return ascWindow.loginModel.ApplicationType;

        try {
            const isMultipleAccount = window.localStorage.getItem('helpMenu.multipleAccounts') === 'true';
            const isOther = window.localStorage.getItem('helpMenu.other') === 'true';
            const isPatient = window.localStorage.getItem('helpMenu.patient') === 'true';
            const isClientAccount = window.localStorage.getItem('helpMenu.clientAccount') === 'true';

            if (!isMultipleAccount && !isOther) {
                if (isClientAccount && !isPatient)
                    return ApplicationEnum.ClientAccount;

                if (isPatient && !isClientAccount)
                    return ApplicationEnum.Patient;
            }
        }
        catch {
            return null;
        }

        return null;
    };

    const buildDefaultHelpMenu = (): LegacyHelpMenuItem[] => {
        switch (getFallbackApplicationType()) {
            case ApplicationEnum.Patient: {
                const items: LegacyHelpMenuItem[] = [];
                if (PageGlobals.layoutModel.ShowMessagesNav) {
                    items.push({
                        text: 'Send Message to Care Team',
                        url: '/patients/PatientMessage?sendMessage=true'
                    });
                    items.push({ separator: true });
                }

                items.push({ text: 'Help', url: 'https://help.chroniccareiq.com/', target: '_blank' });
                return items;
            }

            case ApplicationEnum.ClientAccount: {
                const helpCenterUrl = PageGlobals.layoutModel.Intercom?.HelpCenterUrl;
                return [{
                    text: helpCenterUrl ? 'Support' : 'University',
                    url: helpCenterUrl || '/mains/help/university',
                    target: '_blank'
                }];
            }

            default:
                return [];
        }
    };

    let siteHeaderActionsHost = siteHeaderContent?.querySelector<HTMLElement>('.app-topbar-actions-host') ?? null;
    if (siteHeaderContent && !siteHeaderActionsHost) {
        siteHeaderActionsHost = document.createElement('div');
        siteHeaderContent.appendChild(siteHeaderActionsHost);
    }

    if (siteHeaderActionsHost)
        bindSiteHeaderActions(siteHeaderActionsHost, { titleGroupElement, titleElement });

    const isDesktopViewport = () => window.matchMedia('(min-width: 992px)').matches;

    const getHelpItems = () => {
        const helpItemsObservable = ascWindow.asc?.App?.helpItems;
        if (typeof helpItemsObservable === 'function') {
            const legacyHelpItems = helpItemsObservable() ?? [];
            if (Array.isArray(legacyHelpItems) && legacyHelpItems.length > 0)
                return legacyHelpItems;
        }

        fallbackHelpItems ??= buildDefaultHelpMenu();
        return fallbackHelpItems;
    };

    const getShowHelpItems = () => {
        const showHelpItemsObservable = ascWindow.asc?.App?.showHelpItems;
        if (typeof showHelpItemsObservable === 'function')
            return showHelpItemsObservable();

        return fallbackShowHelpItems;
    };

    const closeSubnav = (subnavItem: HTMLElement) => {
        subnavItem.classList.remove('subnav-open');
        subnavItem.querySelector<HTMLAnchorElement>('.sidebar-nav-link[data-subnav-toggle]')?.setAttribute('aria-expanded', 'false');
    };

    const openSubnav = (subnavItem: HTMLElement) => {
        closeOtherSubnavs(subnavItem);
        subnavItem.classList.add('subnav-open');
        subnavItem.querySelector<HTMLAnchorElement>('.sidebar-nav-link[data-subnav-toggle]')?.setAttribute('aria-expanded', 'true');
    };

    const closeOtherSubnavs = (currentSubnavItem: HTMLElement) => {
        subnavItems
            .filter(subnavItem => subnavItem !== currentSubnavItem)
            .forEach(closeSubnav);
    };

    const buildHelpMenuItem = (helpItem: LegacyHelpMenuItem) => {
        const listItem = document.createElement('li');

        if (helpItem.separator) {
            listItem.className = 'sidebar-subnav-divider';
            return listItem;
        }

        listItem.className = 'sidebar-subnav-item';
        const link = document.createElement('a');
        link.className = 'sidebar-subnav-link';
        link.textContent = helpItem.text ?? 'Help';

        if (helpItem.url)
            link.href = helpItem.url;

        if (helpItem.target)
            link.target = helpItem.target;

        if (helpItem.target === '_blank')
            link.rel = 'noopener noreferrer';

        if (helpItem.enabled === false) {
            link.removeAttribute('href');
            link.setAttribute('aria-disabled', 'true');
        }

        listItem.appendChild(link);
        return listItem;
    };

    const syncHelpMenu = () => {
        if (!helpMenuContainer || !helpMenuList)
            return;

        const helpItems = getHelpItems();
        const showHelpItems = getShowHelpItems();
        const visibleHelpItems = Array.isArray(helpItems)
            ? helpItems.filter(helpItem => helpItem && helpItem.visible !== false)
            : [];

        helpMenuList.replaceChildren();
        visibleHelpItems.forEach(helpItem => helpMenuList.appendChild(buildHelpMenuItem(helpItem)));

        const hasVisibleHelpItems = showHelpItems && visibleHelpItems.length > 0;
        helpMenuContainer.classList.toggle('sidebar-nav-item-hidden', !hasVisibleHelpItems);

        if (!hasVisibleHelpItems)
            closeSubnav(helpMenuContainer);
    };

    if (!toggleButton || !appShell)
        return;

    const phoneViewport = window.matchMedia('(max-width: 767px)');

    const isSidebarCollapsed = () => appShell.classList.contains('sidebar-collapsed');

    const syncMenuState = () => {
        const isCollapsed = appShell.classList.contains('sidebar-collapsed');
        toggleButton.setAttribute('aria-expanded', (!isCollapsed).toString());
        toggleButton.title = isCollapsed ? 'Expand menu' : 'Collapse menu';
        toggleButton.setAttribute('aria-label', toggleButton.title);
        mobileMenuLauncher?.setAttribute('aria-expanded', (!isCollapsed).toString());
        try {
            window.localStorage.setItem('sidebar.collapsed', isCollapsed ? 'true' : 'false');
            document.cookie = 'sidebar.collapsed=' + (isCollapsed ? 'true' : 'false') + '; path=/; max-age=31536000; SameSite=Lax';
        } catch { }
    };

    const handlePhoneViewportChanged = (event: MediaQueryListEvent | MediaQueryList) => {
        if (event.matches) {
            appShell.classList.add('sidebar-collapsed');
        } else {
            const savedState = window.localStorage.getItem('sidebar.collapsed');
            if (savedState === 'true') {
                appShell.classList.add('sidebar-collapsed');
            } else if (savedState === 'false') {
                appShell.classList.remove('sidebar-collapsed');
            }
        }
        subnavItems.forEach(closeSubnav);
        syncMenuState();
    };

    ascWindow.asc?.App?.helpItems?.subscribe?.(syncHelpMenu);
    ascWindow.asc?.App?.showHelpItems?.subscribe?.(syncHelpMenu);
    syncHelpMenu();

    subnavToggles.forEach(subnavToggle => {
        subnavToggle.addEventListener('click', event => {
            const subnavItem = subnavToggle.closest<HTMLElement>('.sidebar-nav-item-with-subnav');

            if (!subnavItem)
                return;

            // When sidebar is expanded (not collapsed), toggle the submenu open/close
            if (!isSidebarCollapsed()) {
                if (subnavItem.classList.contains('subnav-open'))
                    closeSubnav(subnavItem);
                else
                    openSubnav(subnavItem);
            }
            // When sidebar is collapsed (icon-only), navigate to first submenu link
            else {
                const href = subnavToggle.getAttribute('href');
                if (!href || href === '#' || href === 'javascript:void(0)') {
                    const firstSubnavLink = subnavItem.querySelector<HTMLAnchorElement>('.sidebar-subnav-link');
                    if (firstSubnavLink && firstSubnavLink.href) {
                        event.preventDefault();
                        window.location.href = firstSubnavLink.href;
                    }
                }
            }
        });
    });

    subnavItems.forEach(subnavItem => {
        subnavItem.addEventListener('mouseenter', () => {
            // Enable hover on desktop OR when sidebar is collapsed (icon-only mode)
            if (isDesktopViewport() || isSidebarCollapsed())
                openSubnav(subnavItem);
        });

        subnavItem.addEventListener('mouseleave', () => {
            // Enable hover on desktop OR when sidebar is collapsed (icon-only mode)
            if (isDesktopViewport() || isSidebarCollapsed())
                closeSubnav(subnavItem);
        });

        subnavItem.addEventListener('focusin', () => {
            // Enable focus behavior on desktop OR when sidebar is collapsed
            if (isDesktopViewport() || isSidebarCollapsed())
                openSubnav(subnavItem);
        });

        subnavItem.addEventListener('focusout', event => {
            // Enable focus behavior on desktop OR when sidebar is collapsed
            if (!isDesktopViewport() && !isSidebarCollapsed())
                return;

            const nextFocusedElement = event.relatedTarget as Node | null;
            if (nextFocusedElement && subnavItem.contains(nextFocusedElement))
                return;

            closeSubnav(subnavItem);
        });
    });

    document.addEventListener('click', event => {
        const clickTarget = event.target as HTMLElement | null;

        if (clickTarget?.closest('.sidebar-nav-item-with-subnav'))
            return;

        subnavItems.forEach(closeSubnav);
    });

    toggleButton.addEventListener('click', () => {
        appShell.classList.toggle('sidebar-collapsed');

        if (isSidebarCollapsed())
            subnavItems.forEach(closeSubnav);

        syncMenuState();
    });

    mobileMenuLauncher?.addEventListener('click', () => {
        appShell.classList.remove('sidebar-collapsed');
        syncMenuState();
        toggleButton.focus();
    });

    phoneViewport.addEventListener('change', handlePhoneViewportChanged);
    handlePhoneViewportChanged(phoneViewport);
});
